import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProfileAvatar from "@/components/ProfileAvatar";
import MarketplaceBrandFallback from "@/components/marketplace/MarketplaceBrandFallback";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { getLoginUrl, getSignupUrl } from "@/const";
import { getMarketplaceListingHeroImage } from "@/lib/marketplace";
import { toDisplayAssetUrl } from "@/lib/assets";
import { getCatalogFilterPathForProduct } from "@/lib/routes";
import {
  formatMarketChangeAsOf,
  parseResolvedMarketChange,
} from "@/lib/marketChange";
import {
  ALL_FILTER,
  isAggregateFilterValue,
  matchesFallbackDimensionSelection,
} from "@/lib/productDetailFilters";
import MarketProductCard from "@/components/market/MarketProductCard";
import TwoToneHeading from "@/components/market/TwoToneHeading";
import {
  DEVICE_TYPE_LABELS,
  MARKET_NAMES,
  fullDateFormatter,
  getChangeColorClass,
  getProductCardSubtitle,
  getReleaseTimestamp,
  getTrailingMedians,
  type PricePoint,
} from "@/lib/marketDisplay";
import SeoTags from "@/components/SeoTags";
import { useProductAnalytics } from "@/hooks/useProductAnalytics";
import { useIsMobile } from "@/hooks/useMobile";
import { getProductPath } from "@shared/productRouting";
import {
  buildNotFoundSeo,
  buildProductSeo,
  getProductValueSummary,
  isProductCategoryPath,
} from "@shared/seo";
import type { Product } from "@shared/types";
import { useParams, Link, useLocation } from "wouter";
import type { CSSProperties } from "react";
import { lazy, Suspense, useState, useMemo, useEffect, useRef } from "react";
import { useTheme } from "@/contexts/useTheme";

// recharts is the heaviest dependency on this page; load it after the page itself renders.
const ProductPriceChart = lazy(() => import("@/components/ProductPriceChart"));

function formatPrice(cents: number) {
  return `$${cents.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function preventDefault(event: { preventDefault: () => void }) {
  event.preventDefault();
}

function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatChartDateLabel(ts: number) {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function formatDateLabel(value: string | null | undefined) {
  if (!value) return "New listing";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "New listing";

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatSpecLabel(key: string) {
  if (key === "storage") return "Storage";
  if (key === "msrp tiers") return "Launch prices";
  return key
    .split(" ")
    .map(part =>
      part.toLowerCase() === "msrp"
        ? "MSRP"
        : `${part[0]?.toUpperCase() || ""}${part.slice(1)}`
    )
    .join(" ");
}

function formatSpecValue(_key: string, value: string) {
  return value;
}

// "$1,199 (256 GB)" reads as "$1,199 · 256 GB".
function formatMsrpTier(tier: string) {
  return tier.replace(/\s*\(([^)]+)\)\s*$/, " · $1");
}

// Market sales record their configuration as "256 GB / Unlocked"; the carrier follows the storage.
function getSaleCarrier(size: string | null | undefined) {
  if (!size?.includes(" / ")) return null;
  return size.split(" / ").slice(1).join(" / ").trim() || null;
}

function splitMsrpTiers(value: string) {
  if (!value.trim()) return [];
  if (value.includes(" | ")) {
    return value
      .split(" | ")
      .map(tier => tier.trim())
      .filter(Boolean);
  }
  return value
    .split(" - ")
    .map(tier => tier.trim())
    .filter(Boolean);
}

function normalizeStorageLabel(value: string) {
  const compact = value.trim().toUpperCase().replace(/\s+/g, "");
  const match = compact.match(/^(\d+(?:\.\d+)?)(GB|TB)$/);
  if (!match) return value.trim();
  return `${match[1]} ${match[2]}`;
}

function getDisplaySizeSortValue(value: string) {
  const match = value.match(/(\d+(?:\.\d+)?)/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  return Number(match[1]);
}

function humanizeValue(
  value: string | null | undefined,
  fallback = "Not provided"
) {
  if (!value || value.trim().length === 0) return fallback;

  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, character => character.toUpperCase());
}

function formatCarrierLabel(
  value: string | null | undefined,
  fallback = "Not provided"
) {
  if (!value || value.trim().length === 0) return fallback;

  const normalizedValue = value.trim().toLowerCase();

  switch (normalizedValue) {
    case "at&t":
      return "AT&T";
    case "t-mobile":
    case "tmobile":
      return "T-Mobile";
    case "us cellular":
    case "uscellular":
      return "US Cellular";
    case "verizon":
      return "Verizon";
    case "unlocked":
      return "Unlocked";
    default:
      return humanizeValue(value, fallback);
  }
}

function formatConditionLabel(
  value: string | null | undefined,
  fallback = "Not provided"
) {
  if (!value || value.trim().length === 0) return fallback;

  const normalizedValue = value.trim().toLowerCase();

  switch (normalizedValue) {
    case "brand new":
      return "Brand New";
    case "pre-owned":
    case "pre owned":
    case "preowned":
    case "manufacturer refurbished":
    case "very good - refurbished":
    case "good - refurbished":
    case "seller refurbished":
      return "Pre-Owned";
    case "parts only":
    case "for parts":
      return "Parts Only";
    default:
      return humanizeValue(value, fallback);
  }
}

function normalizeConditionFilterValue(value: string | null | undefined) {
  const label = formatConditionLabel(value, "");
  return label === "Not provided" ? "" : label;
}

const CONDITION_FILTER_OPTIONS = ["Brand New", "Pre-Owned", "Parts Only"];

function getTabletConnectivityGroups(rowCarrier: string | null) {
  if (!rowCarrier) return [] as string[];

  const normalized = rowCarrier.trim().toLowerCase();
  if (!normalized) return [] as string[];
  if (normalized === "unlocked") {
    return ["WiFi", "WiFi-Cellular"];
  }
  if (normalized === "wifi" || normalized === "wi-fi") {
    return ["WiFi"];
  }
  if (
    normalized.includes("cellular") ||
    normalized.includes("5g") ||
    normalized.includes("4g") ||
    normalized.includes("lte")
  ) {
    return ["WiFi-Cellular"];
  }

  return [];
}

function matchesTabletConnectivityFilter(
  rowCarrier: string | null,
  selectedCarrier: string
) {
  if (selectedCarrier === ALL_FILTER) return true;
  return getTabletConnectivityGroups(rowCarrier).includes(selectedCarrier);
}

function getUniqueValues(
  values: Array<string | null | undefined>,
  mapValue?: (value: string) => string
) {
  const entries = new Map<string, string>();

  for (const value of values) {
    const trimmedValue = value?.trim();
    if (!trimmedValue) continue;

    const key = trimmedValue.toLowerCase();
    const label = mapValue ? mapValue(trimmedValue) : trimmedValue;
    if (!entries.has(key)) {
      entries.set(key, label);
    }
  }

  return Array.from(entries.values()).sort((left, right) =>
    left.localeCompare(right)
  );
}

function getExpectedStorageOptions(specs: Record<string, string>) {
  const fromMsrpTiers = Array.from(
    (specs["msrp tiers"] || "").matchAll(/\(([^)]+)\)/g),
    match => match[1] || ""
  )
    .map(value => normalizeStorageLabel(value))
    .filter(Boolean);

  if (fromMsrpTiers.length > 0) {
    return Array.from(new Set(fromMsrpTiers));
  }

  return (specs.storage || "")
    .split(",")
    .map(value => normalizeStorageLabel(value))
    .filter(Boolean);
}

function getExpectedDisplaySizeOptions(specs: Record<string, string>) {
  const sizes = Array.from(
    (specs["msrp tiers"] || "").matchAll(/(\d+(?:\.\d+)?)"\s*:/g),
    match => `${match[1]}"`
  );

  if (sizes.length === 0) return [];

  return Array.from(new Set(sizes)).sort((left, right) => {
    const bySize = getDisplaySizeSortValue(left) - getDisplaySizeSortValue(right);
    return bySize !== 0 ? bySize : left.localeCompare(right);
  });
}

const ALLOWED_CARRIERS = ["Unlocked", "AT&T", "T-Mobile", "Verizon"] as const;
const TABLET_CONNECTIVITY_OPTIONS = ["WiFi", "WiFi-Cellular"] as const;
const timeRanges = ["1W", "1M", "3M", "1Y", "ALL"] as const;
const INITIAL_RECENT_SALES_LIMIT = 10;
const MOBILE_INITIAL_RECENT_SALES_LIMIT = 5;
const EXPANDED_RECENT_SALES_LIMIT = 25;
const PRODUCT_PAGE_STATIC_STALE_TIME_MS = 2 * 60 * 1000;
const PRODUCT_PAGE_DYNAMIC_STALE_TIME_MS = 30 * 1000;
const lockedTimeRanges = new Set<(typeof timeRanges)[number]>(["1Y", "ALL"]);
const LIVE_LISTING_PREVIEW_LIMIT = 3;

function isLockedTimeRange(range: (typeof timeRanges)[number]) {
  return lockedTimeRanges.has(range);
}

function FilterButtonGroup({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string;
  options: string[];
  selected: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4"
    >
      <p className="shrink-0 text-[0.8rem] text-[#6E6E73] dark:text-zinc-400 sm:w-[5.25rem]">
        {label}
      </p>
      <div className="flex flex-wrap gap-2">
        {[ALL_FILTER, ...options].map(option => {
          const isActive = selected === option;
          return (
            <button
              key={option}
              type="button"
              aria-pressed={isActive}
              onClick={() => onSelect(option)}
              className={`h-9 rounded-full border px-[0.95rem] text-sm font-medium transition-colors ${
                isActive
                  ? "border-foreground bg-foreground text-background"
                  : "border-[#D2D2D7] text-foreground hover:border-foreground dark:border-white/15"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LiveListingCard({
  listing,
  product,
}: {
  listing: {
    id: number;
    sellerDisplayName?: string | null;
    sellerProfileImageUrl?: string | null;
    sellerRating?: number | null;
    sellerIsVerified?: boolean;
    brand: string | null;
    model: string | null;
    createdAt: string | null;
    price: number | null;
    highestBid: number | null;
    itemCondition: string | null;
    storageCapacity: string | null;
    carrier: string | null;
    images: string[];
    fallbackImageUrl: string | null;
  };
  product: Product;
}) {
  const heroImage = getMarketplaceListingHeroImage(listing);
  const listingPath = `/marketplace/${listing.id}?product=${encodeURIComponent(getProductPath(product))}`;
  const listingTitle =
    [listing.brand, listing.model].filter(Boolean).join(" ") || product.name;
  const details = [
    humanizeValue(listing.itemCondition, ""),
    listing.storageCapacity
      ? normalizeStorageLabel(listing.storageCapacity)
      : null,
    formatCarrierLabel(listing.carrier, ""),
  ]
    .filter(Boolean)
    .join(" · ");
  const sellerName = listing.sellerDisplayName || "techfare seller";
  const sellerMeta = [
    typeof listing.sellerRating === "number"
      ? `${listing.sellerRating.toFixed(1)} rating`
      : null,
    listing.sellerIsVerified ? "Verified" : null,
    `Listed ${formatDateLabel(listing.createdAt)}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Link
      href={listingPath}
      className="grid grid-cols-[3.5rem_minmax(0,1fr)_auto] items-center gap-3.5 rounded-[1.125rem] border border-[#E8E8ED] bg-white p-3.5 transition-colors hover:border-foreground/30 dark:border-white/10 dark:bg-card"
    >
      <span className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl bg-[#F5F5F7] dark:bg-white/[0.06]">
        {heroImage ? (
          <img
            src={heroImage.src}
            alt=""
            draggable={false}
            className={`h-full w-full select-none ${heroImage.fit === "contain" ? "object-contain p-2 mix-blend-multiply dark:mix-blend-normal" : "object-cover"}`}
            onContextMenu={preventDefault}
            onDragStart={preventDefault}
          />
        ) : (
          <MarketplaceBrandFallback brand={listing.brand || product.brand} compact />
        )}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[0.9375rem] font-semibold leading-tight text-foreground">
          {listingTitle}
        </span>
        {details ? (
          <span className="mt-0.5 block truncate text-[0.8125rem] text-[#424245] dark:text-zinc-300">
            {details}
          </span>
        ) : null}
        <span className="mt-1.5 flex min-w-0 items-center gap-1.5 text-[0.75rem] text-[#6E6E73] dark:text-zinc-400">
          <ProfileAvatar
            name={sellerName}
            imageUrl={listing.sellerProfileImageUrl}
            className="size-5 shrink-0 text-foreground"
            fallbackClassName="bg-[#F5F5F7] text-[10px] font-semibold text-[#424245] dark:bg-white/10 dark:text-zinc-300"
          />
          <span className="truncate">
            {sellerName} · {sellerMeta}
          </span>
        </span>
      </span>
      <span className="text-right">
        <span className="block font-mono text-[0.9375rem] font-medium text-foreground">
          {listing.price == null ? "—" : formatPrice(listing.price)}
        </span>
        <span className="mt-0.5 block text-[0.75rem] text-[#6E6E73] dark:text-zinc-400">
          {listing.highestBid == null ? (
            "No bids yet"
          ) : (
            <>
              Bid <span className="font-mono">{formatPrice(listing.highestBid)}</span>
            </>
          )}
        </span>
      </span>
    </Link>
  );
}

export default function ProductDetail() {
  const isMobile = useIsMobile();
  const params = useParams<{ category?: string; slug: string }>();
  const [location, setLocation] = useLocation();
  const { theme } = useTheme();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const profileQuery = trpc.auth.profile.useQuery(undefined, {
    enabled: !authLoading && isAuthenticated,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [timeRange, setTimeRange] = useState<(typeof timeRanges)[number]>("3M");
  const [showAllRecentSales, setShowAllRecentSales] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState(ALL_FILTER);
  const [selectedStorage, setSelectedStorage] = useState(ALL_FILTER);
  const [selectedCarrier, setSelectedCarrier] = useState(ALL_FILTER);
  const [selectedDisplaySize, setSelectedDisplaySize] = useState(ALL_FILTER);
  const [selectedListingCondition, setSelectedListingCondition] =
    useState(ALL_FILTER);
  const [selectedListingStorage, setSelectedListingStorage] =
    useState(ALL_FILTER);
  const [selectedListingCarrier, setSelectedListingCarrier] =
    useState(ALL_FILTER);
  const [isLiveListingsOpen, setIsLiveListingsOpen] = useState(false);
  const [
    hasInitializedFilteredSelections,
    setHasInitializedFilteredSelections,
  ] = useState(false);
  const loggedViewProductIdRef = useRef<number | null>(null);
  const { trackProductView, trackProductClick } = useProductAnalytics();

  const hasExtendedHistoryAccess = isAuthenticated;
  const hasRecentSalesAccess = isAuthenticated;
  const hasAdminAccess = Boolean(
    profileQuery.data?.isAdmin || profileQuery.data?.role === "admin"
  );
  const initialRecentSalesLimit = isMobile
    ? MOBILE_INITIAL_RECENT_SALES_LIMIT
    : INITIAL_RECENT_SALES_LIMIT;
  const effectiveTimeRange =
    !hasExtendedHistoryAccess && isLockedTimeRange(timeRange)
      ? "3M"
      : timeRange;
  const recentSalesLimit = showAllRecentSales
    ? EXPANDED_RECENT_SALES_LIMIT
    : initialRecentSalesLimit;

  const { data: product, isLoading, isError: productLoadFailed } = trpc.products.bySlug.useQuery(
    { slug: params.slug || "" },
    {
      staleTime: PRODUCT_PAGE_STATIC_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (!product) return;
    const pathname = location.split("?")[0] || "/";
    if (!pathname.startsWith("/product/") && !isProductCategoryPath(pathname)) {
      return;
    }
    const canonicalPath = getProductPath(product);
    if (location !== canonicalPath) {
      // Canonicalize legacy product URLs without adding an extra history entry.
      setLocation(canonicalPath, { replace: true });
    }
  }, [location, product, setLocation]);

  const productId = product?.id;

  useEffect(() => {
    if (!productId || loggedViewProductIdRef.current === productId) {
      return;
    }

    loggedViewProductIdRef.current = productId;
    trackProductView(productId, "product-detail");
  }, [productId, trackProductView]);
  const { data: filteredPrices } = trpc.products.filteredPrices.useQuery(
    { productId: productId! },
    {
      enabled:
        !!productId &&
        (product?.deviceType === "phones" ||
          product?.deviceType === "tablets" ||
          product?.deviceType === "vr_headsets"),
      staleTime: PRODUCT_PAGE_STATIC_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: productMarketChanges,
    isFetched: hasFetchedProductMarketChanges,
  } = trpc.products.marketChanges.useQuery(
    { productIds: productId ? [productId] : [] },
    {
      enabled: !!productId,
      staleTime: PRODUCT_PAGE_DYNAMIC_STALE_TIME_MS,
      refetchOnWindowFocus: false,
      placeholderData: previousData => previousData,
    }
  );

  const { data: priceHistory } = trpc.market.priceHistory.useQuery(
    { productId: productId!, range: effectiveTimeRange },
    {
      enabled: !!productId,
      staleTime: PRODUCT_PAGE_STATIC_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    }
  );

  const { data: recentSales } = trpc.market.recentSales.useQuery(
    { productId: productId!, limit: recentSalesLimit },
    {
      enabled: !!productId && hasRecentSalesAccess,
      staleTime: PRODUCT_PAGE_DYNAMIC_STALE_TIME_MS,
      refetchOnWindowFocus: false,
      placeholderData: previousData => previousData,
    }
  );

  const { data: marketSummary } = trpc.market.summary.useQuery(
    { productId: productId! },
    {
      enabled: !!productId,
      staleTime: PRODUCT_PAGE_DYNAMIC_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    }
  );

  const { data: liveListingsData } = trpc.marketplace.byProduct.useQuery(
    { productId: productId! },
    {
      enabled: !!productId,
      staleTime: PRODUCT_PAGE_DYNAMIC_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    }
  );

  const { data: related } = trpc.products.related.useQuery(
    { productId: productId!, categoryId: product?.categoryId!, limit: 5 },
    {
      enabled: !!productId && !!product?.categoryId,
      staleTime: PRODUCT_PAGE_STATIC_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    }
  );

  const relatedProducts = useMemo(
    () => (related ?? []).filter((relatedProduct): relatedProduct is Product => relatedProduct != null),
    [related]
  );
  const { data: relatedHistories, isLoading: relatedHistoriesLoading } =
    trpc.market.recentPriceHistories.useQuery(
      { productIds: relatedProducts.map(relatedProduct => relatedProduct.id) },
      {
        enabled: relatedProducts.length > 0,
        staleTime: PRODUCT_PAGE_STATIC_STALE_TIME_MS,
        refetchOnWindowFocus: false,
      }
    );
  const relatedHistoriesById = useMemo(
    () =>
      new Map<number, PricePoint[]>(
        (relatedHistories ?? []).map(entry => [entry.productId, entry.points])
      ),
    [relatedHistories]
  );

  const expectedStorageOptions = useMemo(() => {
    const specs = (product?.specs as Record<string, string> | null) || {};
    return getExpectedStorageOptions(specs);
  }, [product?.specs]);

  const expectedDisplaySizeOptions = useMemo(() => {
    const specs = (product?.specs as Record<string, string> | null) || {};
    return getExpectedDisplaySizeOptions(specs);
  }, [product?.specs]);

  const filteredPriceRows = useMemo(() => {
    if (!filteredPrices) return [];
    if (!expectedStorageOptions.length && !expectedDisplaySizeOptions.length) {
      return filteredPrices;
    }

    const allowedStorages = new Set(expectedStorageOptions);
    const allowedDisplaySizes = new Set(expectedDisplaySizeOptions);
    return filteredPrices.filter(
      row =>
        (!expectedStorageOptions.length ||
          isAggregateFilterValue(row.storage) ||
          allowedStorages.has(row.storage)) &&
        (!expectedDisplaySizeOptions.length ||
          isAggregateFilterValue(row.displaySize ?? null) ||
          allowedDisplaySizes.has(row.displaySize ?? ""))
    );
  }, [expectedDisplaySizeOptions, expectedStorageOptions, filteredPrices]);

  const sortedPriceHistory = useMemo(
    () =>
      (priceHistory ?? [])
        .filter(point => point.price > 0)
        .sort((a, b) => a.recordedAt - b.recordedAt),
    [priceHistory]
  );
  const chartTrendDays = sortedPriceHistory.length >= 21 ? 7 : 3;
  const chartTrend = useMemo(
    () =>
      getTrailingMedians(
        sortedPriceHistory.map(point => point.price),
        chartTrendDays
      ),
    [chartTrendDays, sortedPriceHistory]
  );

  const chartData = useMemo(() => {
    // Downsample for performance
    const step = Math.max(1, Math.floor(sortedPriceHistory.length / 120));
    return sortedPriceHistory
      .map((point, index) => ({
        date: point.recordedAt,
        price: point.price,
        trend: chartTrend[index],
      }))
      .filter((_, i) => i % step === 0 || i === sortedPriceHistory.length - 1);
  }, [chartTrend, sortedPriceHistory]);

  const priceStats = useMemo(() => {
    if (chartTrend.length === 0) return null;
    const first = chartTrend[0];
    const last = chartTrend[chartTrend.length - 1];
    const change = last - first;
    return {
      high: Math.max(...chartTrend),
      low: Math.min(...chartTrend),
      avg: Math.round(
        sortedPriceHistory.reduce((total, point) => total + point.price, 0) /
          sortedPriceHistory.length
      ),
      change,
      changePercent: Number(((change / first) * 100).toFixed(1)),
    };
  }, [chartTrend, sortedPriceHistory]);

  const marketStats = useMemo(() => {
    if (!product) return null;

    return {
      totalSales: marketSummary?.totalSales ?? product.totalSales,
      weekHigh52:
        marketSummary?.weekHigh52 ??
        product.weekHigh52 ??
        (product.currentPrice > 0 ? product.currentPrice : null),
      weekLow52:
        marketSummary?.weekLow52 ??
        product.weekLow52 ??
        (product.currentPrice > 0 ? product.currentPrice : null),
      volatility:
        marketSummary?.volatility ?? (Number(product.volatility) || 0),
    };
  }, [marketSummary, product]);

  const visibleRecentSales = useMemo(() => recentSales || [], [recentSales]);

  useEffect(() => {
    setSelectedCondition(ALL_FILTER);
    setSelectedStorage(ALL_FILTER);
    setSelectedCarrier(ALL_FILTER);
    setSelectedDisplaySize(ALL_FILTER);
    setSelectedListingCondition(ALL_FILTER);
    setSelectedListingStorage(ALL_FILTER);
    setSelectedListingCarrier(ALL_FILTER);
    setHasInitializedFilteredSelections(false);
  }, [productId]);

  const liveListings = useMemo(
    () => liveListingsData ?? [],
    [liveListingsData]
  );

  const listingConditionOptions = useMemo(
    () =>
      getUniqueValues(
        liveListings.map(listing => listing.itemCondition),
        value => humanizeValue(value)
      ),
    [liveListings]
  );

  const listingStorageOptions = useMemo(
    () =>
      getUniqueValues(
        liveListings.map(listing => listing.storageCapacity),
        value => normalizeStorageLabel(value)
      ),
    [liveListings]
  );

  const listingCarrierOptions = useMemo(
    () =>
      getUniqueValues(
        liveListings.map(listing => listing.carrier),
        value => formatCarrierLabel(value)
      ),
    [liveListings]
  );

  const visibleLiveListings = useMemo(() => {
    return liveListings.filter(listing => {
      const listingCondition = humanizeValue(listing.itemCondition);
      const listingStorage = listing.storageCapacity
        ? normalizeStorageLabel(listing.storageCapacity)
        : "";
      const listingCarrier = formatCarrierLabel(listing.carrier, "");

      if (
        selectedListingCondition !== ALL_FILTER &&
        listingCondition !== selectedListingCondition
      ) {
        return false;
      }

      if (
        selectedListingStorage !== ALL_FILTER &&
        listingStorage !== selectedListingStorage
      ) {
        return false;
      }

      if (
        selectedListingCarrier !== ALL_FILTER &&
        listingCarrier !== selectedListingCarrier
      ) {
        return false;
      }

      return true;
    });
  }, [
    liveListings,
    selectedListingCarrier,
    selectedListingCondition,
    selectedListingStorage,
  ]);

  const previewLiveListings = useMemo(
    () => visibleLiveListings.slice(0, LIVE_LISTING_PREVIEW_LIMIT),
    [visibleLiveListings]
  );
  const productImageSrc = toDisplayAssetUrl(product?.imageUrl);

  const hasActiveListingFilters =
    selectedListingCondition !== ALL_FILTER ||
    selectedListingStorage !== ALL_FILTER ||
    selectedListingCarrier !== ALL_FILTER;

  function resetListingFilters() {
    setSelectedListingCondition(ALL_FILTER);
    setSelectedListingStorage(ALL_FILTER);
    setSelectedListingCarrier(ALL_FILTER);
  }

  const liveListingStats = useMemo(() => {
    const askPrices = liveListings
      .map(listing => listing.price)
      .filter(
        (value): value is number =>
          typeof value === "number" && Number.isFinite(value) && value > 0
      );
    const bidPrices = liveListings
      .map(listing => listing.highestBid)
      .filter(
        (value): value is number =>
          typeof value === "number" && Number.isFinite(value) && value > 0
      );

    return {
      lowestAsk: askPrices.length > 0 ? Math.min(...askPrices) : null,
      highestBid: bidPrices.length > 0 ? Math.max(...bidPrices) : null,
      total: liveListings.length,
    };
  }, [liveListings]);

  const conditionOptions = useMemo(() => {
    const available = new Set(
      filteredPriceRows
        .map(row => formatConditionLabel(row.condition, ""))
        .filter(Boolean)
    );
    return CONDITION_FILTER_OPTIONS.filter(option => available.has(option));
  }, [filteredPriceRows]);

  useEffect(() => {
    if (!conditionOptions.length || hasInitializedFilteredSelections) return;
    if (conditionOptions.includes("Pre-Owned")) {
      setSelectedCondition("Pre-Owned");
    }
    setHasInitializedFilteredSelections(true);
  }, [conditionOptions, hasInitializedFilteredSelections]);

  const storageOptions = useMemo(() => {
    const options = new Set<string>();
    for (const row of filteredPriceRows) {
      if (
        selectedCondition !== ALL_FILTER &&
        normalizeConditionFilterValue(row.condition) !== selectedCondition
      )
        continue;
      if (
        selectedDisplaySize !== ALL_FILTER &&
        row.displaySize !== selectedDisplaySize
      )
        continue;
      if (
        product?.deviceType === "tablets"
          ? !matchesTabletConnectivityFilter(row.carrier, selectedCarrier)
          : selectedCarrier !== ALL_FILTER && row.carrier !== selectedCarrier
      )
        continue;
      if (row.storage && !isAggregateFilterValue(row.storage))
        options.add(row.storage);
    }

    if (expectedStorageOptions.length > 0) {
      const hasScopedSelection =
        selectedCondition !== ALL_FILTER ||
        selectedDisplaySize !== ALL_FILTER ||
        selectedCarrier !== ALL_FILTER;
      const scopedOptions = expectedStorageOptions.filter(option =>
        options.has(option)
      );

      if (hasScopedSelection && scopedOptions.length > 0) {
        return scopedOptions;
      }

      return expectedStorageOptions;
    }

    return Array.from(options);
  }, [
    expectedStorageOptions,
    filteredPriceRows,
    selectedCarrier,
    selectedCondition,
    selectedDisplaySize,
  ]);

  const carrierOptions = useMemo(() => {
    const options = new Set<string>();
    for (const row of filteredPriceRows) {
      if (
        selectedCondition !== ALL_FILTER &&
        normalizeConditionFilterValue(row.condition) !== selectedCondition
      )
        continue;
      if (
        selectedDisplaySize !== ALL_FILTER &&
        row.displaySize !== selectedDisplaySize
      )
        continue;
      if (selectedStorage !== ALL_FILTER && row.storage !== selectedStorage)
        continue;
      if (row.carrier && !isAggregateFilterValue(row.carrier)) {
        if (product?.deviceType === "tablets") {
          for (const connectivity of getTabletConnectivityGroups(row.carrier)) {
            options.add(connectivity);
          }
        } else {
          options.add(row.carrier);
        }
      }
    }
    if (product?.deviceType === "tablets") {
      return TABLET_CONNECTIVITY_OPTIONS.filter(option => options.has(option));
    }
    return ALLOWED_CARRIERS.filter(carrier => options.has(carrier)) as string[];
  }, [
    filteredPriceRows,
    product?.deviceType,
    selectedCondition,
    selectedDisplaySize,
    selectedStorage,
  ]);

  const displaySizeOptions = useMemo(() => {
    if (expectedDisplaySizeOptions.length > 0) {
      return expectedDisplaySizeOptions;
    }

    const options = new Set<string>();
    for (const row of filteredPriceRows) {
      if (
        selectedCondition !== ALL_FILTER &&
        normalizeConditionFilterValue(row.condition) !== selectedCondition
      )
        continue;
      if (selectedStorage !== ALL_FILTER && row.storage !== selectedStorage)
        continue;
      if (
        product?.deviceType === "tablets"
          ? !matchesTabletConnectivityFilter(row.carrier, selectedCarrier)
          : selectedCarrier !== ALL_FILTER && row.carrier !== selectedCarrier
      )
        continue;
      if (row.displaySize && !isAggregateFilterValue(row.displaySize))
        options.add(row.displaySize);
    }
    return Array.from(options).sort((left, right) => {
      const bySize =
        getDisplaySizeSortValue(left) - getDisplaySizeSortValue(right);
      return bySize !== 0 ? bySize : left.localeCompare(right);
    });
  }, [
    expectedDisplaySizeOptions,
    filteredPriceRows,
    product?.deviceType,
    selectedCarrier,
    selectedCondition,
    selectedStorage,
  ]);
  const hasDisplaySizeDimension =
    expectedDisplaySizeOptions.length > 0 ||
    filteredPriceRows.some(
      row => !isAggregateFilterValue(row.displaySize ?? null)
    );
  const hasStorageDimension =
    expectedStorageOptions.length > 0 ||
    filteredPriceRows.some(row => !isAggregateFilterValue(row.storage));
  const hasCarrierDimension =
    product?.deviceType === "tablets" ||
    filteredPriceRows.some(row => !isAggregateFilterValue(row.carrier));

  useEffect(() => {
    if (selectedStorage === ALL_FILTER) return;
    if (storageOptions.includes(selectedStorage)) return;
    setSelectedStorage(ALL_FILTER);
  }, [selectedStorage, storageOptions]);

  useEffect(() => {
    if (selectedCarrier === ALL_FILTER) return;
    if (carrierOptions.includes(selectedCarrier)) return;
    setSelectedCarrier(ALL_FILTER);
  }, [carrierOptions, selectedCarrier]);

  useEffect(() => {
    if (selectedDisplaySize === ALL_FILTER) return;
    if (displaySizeOptions.includes(selectedDisplaySize)) return;
    setSelectedDisplaySize(ALL_FILTER);
  }, [displaySizeOptions, selectedDisplaySize]);

  useEffect(() => {
    if (hasExtendedHistoryAccess) return;
    if (!isLockedTimeRange(timeRange)) return;
    setTimeRange("3M");
  }, [hasExtendedHistoryAccess, timeRange]);

  const matchingFilteredPrices = useMemo(() => {
    const concreteRows = filteredPriceRows.filter(row => {
      const conditionMatches =
        selectedCondition === ALL_FILTER
          ? !isAggregateFilterValue(row.condition)
          : normalizeConditionFilterValue(row.condition) === selectedCondition;
      const storageMatches = matchesFallbackDimensionSelection(
        selectedStorage,
        row.storage,
        hasStorageDimension
      );
      const displaySizeMatches = matchesFallbackDimensionSelection(
        selectedDisplaySize,
        row.displaySize,
        hasDisplaySizeDimension
      );
      const carrierMatches =
        product?.deviceType === "tablets"
          ? selectedCarrier === ALL_FILTER
            ? !isAggregateFilterValue(row.carrier)
            : matchesTabletConnectivityFilter(row.carrier, selectedCarrier)
          : matchesFallbackDimensionSelection(
              selectedCarrier,
              row.carrier,
              hasCarrierDimension
            );

      return (
        conditionMatches &&
        storageMatches &&
        displaySizeMatches &&
        carrierMatches
      );
    });
    const hasExplicitSelection =
      selectedCondition !== ALL_FILTER ||
      selectedStorage !== ALL_FILTER ||
      selectedDisplaySize !== ALL_FILTER ||
      selectedCarrier !== ALL_FILTER;

    if (hasExplicitSelection && concreteRows.length > 0) {
      return concreteRows;
    }

    const exactRows = filteredPriceRows.filter(row => {
      const conditionMatches =
        selectedCondition === ALL_FILTER
          ? isAggregateFilterValue(row.condition)
          : normalizeConditionFilterValue(row.condition) === selectedCondition;
      const storageMatches =
        selectedStorage === ALL_FILTER
          ? isAggregateFilterValue(row.storage)
          : row.storage === selectedStorage;
      const displaySizeMatches =
        selectedDisplaySize === ALL_FILTER
          ? isAggregateFilterValue(row.displaySize ?? null)
          : row.displaySize === selectedDisplaySize;
      const carrierMatches =
        product?.deviceType === "tablets"
          ? selectedCarrier === ALL_FILTER
            ? isAggregateFilterValue(row.carrier)
            : matchesTabletConnectivityFilter(row.carrier, selectedCarrier)
          : selectedCarrier === ALL_FILTER
            ? isAggregateFilterValue(row.carrier)
            : row.carrier === selectedCarrier;

      return (
        conditionMatches &&
        storageMatches &&
        displaySizeMatches &&
        carrierMatches
      );
    });
    if (exactRows.length > 0) {
      return exactRows;
    }

    return concreteRows;
  }, [
    filteredPriceRows,
    product?.deviceType,
    selectedCarrier,
    selectedCondition,
    selectedDisplaySize,
    selectedStorage,
    hasDisplaySizeDimension,
    hasStorageDimension,
    hasCarrierDimension,
  ]);

  const filteredMarketPrice = useMemo(() => {
    if (!matchingFilteredPrices.length) return null;
    if (matchingFilteredPrices.length === 1)
      return matchingFilteredPrices[0].avgPrice;

    const total = matchingFilteredPrices.reduce(
      (sum, row) => sum + row.avgPrice,
      0
    );
    return Number((total / matchingFilteredPrices.length).toFixed(2));
  }, [matchingFilteredPrices]);

  const hasFilteredPriceControls =
    filteredPriceRows.length > 0 ||
    expectedStorageOptions.length > 0 ||
    expectedDisplaySizeOptions.length > 0;
  const shouldUseModelAveragePrice =
    hasFilteredPriceControls &&
    selectedCondition === "Pre-Owned" &&
    selectedStorage === ALL_FILTER &&
    selectedDisplaySize === ALL_FILTER &&
    selectedCarrier === ALL_FILTER;
  const displayedFilteredPrice = shouldUseModelAveragePrice
    ? (product?.currentPrice ?? null)
    : filteredMarketPrice;
  const recentSalesComparisonPrice = useMemo(() => {
    if (filteredMarketPrice != null && filteredMarketPrice > 0) {
      return filteredMarketPrice;
    }

    if ((product?.currentPrice ?? 0) > 0) {
      return product?.currentPrice ?? null;
    }

    const latestHistoryPrice = priceHistory?.[priceHistory.length - 1]?.price ?? null;
    if (latestHistoryPrice != null && latestHistoryPrice > 0) {
      return latestHistoryPrice;
    }

    return null;
  }, [filteredMarketPrice, priceHistory, product?.currentPrice]);
  const activeSelectionLabel = [
    selectedCondition !== ALL_FILTER ? selectedCondition : null,
    selectedStorage !== ALL_FILTER ? selectedStorage : null,
    selectedDisplaySize !== ALL_FILTER ? selectedDisplaySize : null,
    selectedCarrier !== ALL_FILTER ? selectedCarrier : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const canShowMoreRecentSales =
    showAllRecentSales ||
    (recentSales?.length ?? 0) >= initialRecentSalesLimit;
  const mockRecentSales = useMemo(() => {
    if (!product || product.currentPrice <= 0) return [];

    const dayOffsets = [1, 3, 5, 8, 12, 16];
    const priceMultipliers = [1.03, 0.99, 1.01, 0.97, 1.05, 1.0];
    const conditions = [
      "Pre-Owned",
      "Like New",
      "Good",
      "Fair",
      "Brand New",
      "Good",
    ];

    return dayOffsets.map((dayOffset, index) => {
      const soldAt = new Date();
      soldAt.setDate(soldAt.getDate() - dayOffset);

      const price = Math.round(product.currentPrice * priceMultipliers[index]);
      const diff = price - product.currentPrice;
      const diffPercent = ((diff / product.currentPrice) * 100).toFixed(1);

      return {
        id: `mock-sale-${index}`,
        condition: conditions[index] ?? "Pre-Owned",
        storage: null,
        soldAt: soldAt.toISOString(),
        price,
        diffPercent: Number(diffPercent),
      };
    });
  }, [product]);

  // Leave the server-rendered SEO tags alone while loading or when the request fails;
  // only a confirmed missing product should switch the page to noindex.
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="container flex-1 pt-5 md:pt-6">
          <div className="animate-pulse">
            <div className="h-4 w-48 rounded-full bg-[#F5F5F7] dark:bg-white/[0.06]" />
            <div className="mt-5 grid gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-14">
              <div className="aspect-[1.1] rounded-[1.75rem] bg-[#F5F5F7] dark:bg-white/[0.04] sm:aspect-square" />
              <div className="space-y-4">
                <div className="h-4 w-40 rounded-full bg-[#F5F5F7] dark:bg-white/[0.06]" />
                <div className="h-11 w-3/4 rounded-xl bg-[#F5F5F7] dark:bg-white/[0.06]" />
                <div className="h-5 w-2/3 rounded-full bg-[#F5F5F7] dark:bg-white/[0.06]" />
                <div className="mt-8 h-72 rounded-[1.25rem] bg-[#F5F5F7] dark:bg-white/[0.04]" />
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        {productLoadFailed ? null : (
          <SeoTags
            metadata={
              typeof window === "undefined"
                ? buildNotFoundSeo()
                : buildNotFoundSeo(window.location.origin)
            }
          />
        )}
        <Navbar />
        <main className="container flex-1 py-20 text-center">
          <h2 className="text-xl font-bold text-foreground">
            {productLoadFailed ? "We couldn't load this product right now" : "Product not found"}
          </h2>
          <Link href="/" className="text-primary mt-4 inline-block">
            Back Home
          </Link>
        </main>
      </div>
    );
  }

  const liveMarketChange = productMarketChanges?.[0];
  const seoMetadata =
    typeof window === "undefined"
      ? buildProductSeo({ product })
      : buildProductSeo({ origin: window.location.origin, product });
  const priceChangeAmount = parseResolvedMarketChange(
    hasFetchedProductMarketChanges ? liveMarketChange?.priceChange24h ?? null : null
  );
  const changePercent = parseResolvedMarketChange(
    hasFetchedProductMarketChanges
      ? liveMarketChange?.priceChangePercent24h ?? null
      : null
  );
  const priceChangeAsOf = hasFetchedProductMarketChanges
    ? liveMarketChange?.priceChangeAsOf ?? null
    : null;
  const marketChangeAsOfLabel = formatMarketChangeAsOf(priceChangeAsOf, {
    includeYear: true,
  });
  const hasResolvedChange =
    priceChangeAmount.hasResolvedChange && changePercent.hasResolvedChange;
  const resolvedPriceChangeAmount = priceChangeAmount.value ?? 0;
  const resolvedChangePercent = changePercent.value ?? 0;
  const isPositive = resolvedChangePercent >= 0;
  const hasCurrentMarketPrice = product.currentPrice > 0;
  const specs = (product.specs as Record<string, string>) || {};
  const hasMsrpTiers = Boolean(specs["msrp tiers"]);
  const marketPath = getCatalogFilterPathForProduct(product);
  const sellThisProductHref =
    `/sell?slug=${encodeURIComponent(product.slug)}` +
    `&product=${encodeURIComponent(product.marketModel)}`;
  const hasLowestAskAvailable =
    liveListingStats.lowestAsk != null || product.lowestAsk != null;
  const adminEditHref = hasAdminAccess
    ? `/admin/product-builder?slug=${encodeURIComponent(product.slug)}`
    : null;
  const chartColors =
    theme === "dark"
      ? {
          grid: "rgba(255, 255, 255, 0.06)",
          tick: "#A1A1AA",
          tooltipBg: "oklch(0.17 0.005 260)",
          tooltipBorder: "rgba(255, 255, 255, 0.1)",
          tooltipText: "oklch(0.95 0.005 260)",
          activeDotStroke: "oklch(0.17 0.005 260)",
          trend: "#5B8CF0",
          daily: "rgba(91, 140, 240, 0.22)",
        }
      : {
          grid: "#F0F0F3",
          tick: "#86868B",
          tooltipBg: "#FFFFFF",
          tooltipBorder: "#E8E8ED",
          tooltipText: "#111113",
          activeDotStroke: "#FFFFFF",
          trend: "#2F66D4",
          daily: "#D3DEF5",
        };
  // Same answer copy the server renders, so Google sees it after JavaScript replaces the server HTML.
  const valueSummary = getProductValueSummary(product);
  const topConfiguration = (filteredPrices ?? []).reduce<{ storage: string; price: number } | null>(
    (top, entry) =>
      entry.storage && entry.avgPrice > (top?.price ?? 0)
        ? { storage: entry.storage, price: entry.avgPrice }
        : top,
    null
  );
  const formatWholeDollars = (value: number) => `$${Math.round(value).toLocaleString("en-US")}`;
  const valueFaq = [
    valueSummary.priceLabel
      ? {
          question: `How much is the ${valueSummary.modelName} worth?`,
          answer: `About ${valueSummary.priceLabel} used as of ${valueSummary.monthLabel}. Storage and condition move the price the most.`,
        }
      : null,
    valueSummary.percentOfRetail != null && valueSummary.retailPriceLabel
      ? {
          question: `How well does the ${valueSummary.modelName} hold its value?`,
          answer: `It keeps about ${valueSummary.percentOfRetail}% of its ${valueSummary.retailPriceLabel} launch price.`,
        }
      : null,
    topConfiguration
      ? {
          question: `Which ${valueSummary.modelName} configuration sells for the most?`,
          answer: `The ${topConfiguration.storage} version, at about ${formatWholeDollars(topConfiguration.price)}.`,
        }
      : null,
  ].filter((item): item is { question: string; answer: string } => item != null);
  const marketSlug = marketPath.split("/")[2] ?? "";
  const marketTitle = MARKET_NAMES[marketSlug]?.title ?? null;
  const released = getReleaseTimestamp(product);
  // Consoles and headphones have no per-configuration prices, so there's nothing to switch.
  const canSwitchPrices = hasFilteredPriceControls && filteredPriceRows.length > 0;
  const displayedPrice = canSwitchPrices
    ? displayedFilteredPrice
    : hasCurrentMarketPrice
      ? product.currentPrice
      : null;
  const priceNote = !canSwitchPrices
    ? null
    : displayedFilteredPrice == null
      ? "No sales for this configuration yet."
      : shouldUseModelAveragePrice
        ? "The model's market price, from recent used sales."
        : matchingFilteredPrices.length > 1
          ? `Average of ${matchingFilteredPrices.length} matching configurations.`
          : "From recent sales of this configuration.";
  const vsLaunchPrice =
    product.retailPrice && hasCurrentMarketPrice
      ? ((product.currentPrice - product.retailPrice) / product.retailPrice) * 100
      : null;
  const lowestAsk = liveListingStats.lowestAsk ?? product.lowestAsk;
  const highestBid = liveListingStats.highestBid ?? product.highestBid;
  const showsSaleCarrier = visibleRecentSales.some(sale => getSaleCarrier(sale.size) != null);
  const recentSalesColumns = [
    "4.5rem",
    "minmax(0,1fr)",
    hasStorageDimension ? "5.5rem" : null,
    showsSaleCarrier ? "8rem" : null,
    "7rem",
    "5.5rem",
  ]
    .filter(Boolean)
    .join(" ");
  const signedOutRecentSalesColumns = [
    "4.5rem",
    "minmax(0,1fr)",
    hasStorageDimension ? "5.5rem" : null,
    "7rem",
    "5.5rem",
  ]
    .filter(Boolean)
    .join(" ");
  const pillClassName =
    "inline-flex h-[2.625rem] items-center justify-center rounded-full px-5 text-sm font-medium transition-colors";
  const productHeader = (
    <div>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-[#6E6E73] dark:text-zinc-400">
          {[
            DEVICE_TYPE_LABELS[product.deviceType],
            released == null ? null : `Released ${fullDateFormatter.format(released)}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {adminEditHref ? (
          <Link
            href={adminEditHref}
            className="inline-flex h-8 shrink-0 items-center rounded-full border border-[#D2D2D7] px-3.5 text-xs font-medium text-foreground transition-colors hover:border-foreground dark:border-white/15"
          >
            Edit product
          </Link>
        ) : null}
      </div>
      <h1 className="mt-1.5 text-[1.875rem] font-semibold leading-[1.08] tracking-[-0.035em] text-foreground sm:text-[2.75rem]">
        {product.name}
      </h1>
      {valueSummary.priceLabel ? (
        <p className="mt-3 max-w-[40rem] text-[0.95rem] leading-7 text-[#424245] dark:text-zinc-300 sm:text-base">
          A used {valueSummary.modelName} sells for about{" "}
          <span className="font-semibold text-foreground">{valueSummary.priceLabel}</span> (
          {valueSummary.monthLabel})
          {valueSummary.percentOfRetail != null && valueSummary.retailPriceLabel
            ? `, ${valueSummary.percentOfRetail}% of its ${valueSummary.retailPriceLabel} launch price`
            : ""}
          .
        </p>
      ) : null}
    </div>
  );
  const detailRowClassName =
    "flex items-start justify-between gap-4 border-t border-[#F0F0F3] py-2.5 dark:border-white/[0.06]";
  const productDetailsCard = (
    <section
      aria-labelledby="product-details"
      className="rounded-[1.25rem] border border-[#E8E8ED] px-6 py-5 dark:border-white/10"
    >
      <h2 id="product-details" className="text-[0.95rem] font-semibold text-foreground">
        Details
      </h2>
      <dl className="mt-3 text-sm">
        <div className={detailRowClassName}>
          <dt className="text-[#6E6E73] dark:text-zinc-400">Brand</dt>
          <dd className="text-right text-foreground">{product.brand}</dd>
        </div>
        {released != null ? (
          <div className={detailRowClassName}>
            <dt className="text-[#6E6E73] dark:text-zinc-400">Released</dt>
            <dd className="text-right text-foreground">{fullDateFormatter.format(released)}</dd>
          </div>
        ) : null}
        {product.retailPrice && !hasMsrpTiers ? (
          <div className={detailRowClassName}>
            <dt className="text-[#6E6E73] dark:text-zinc-400">Launch price</dt>
            <dd className="text-right font-mono text-foreground">
              {formatPrice(product.retailPrice)}
            </dd>
          </div>
        ) : null}
        {Object.entries(specs).map(([key, value]) =>
          key === "category" || key === "carrier" ? null : key === "msrp tiers" ? (
            <div
              key={key}
              className="border-t border-[#F0F0F3] pt-2.5 dark:border-white/[0.06]"
            >
              <dt className="text-[#6E6E73] dark:text-zinc-400">{formatSpecLabel(key)}</dt>
              <dd className="mt-2.5 flex flex-wrap gap-1.5">
                {splitMsrpTiers(value).map(tier => (
                  <span
                    key={tier}
                    className="rounded-full bg-[#F5F5F7] px-2.5 py-1 font-mono text-xs text-foreground dark:bg-white/[0.06]"
                  >
                    {formatMsrpTier(tier)}
                  </span>
                ))}
              </dd>
            </div>
          ) : (
            <div key={key} className={detailRowClassName}>
              <dt className="shrink-0 text-[#6E6E73] dark:text-zinc-400">{formatSpecLabel(key)}</dt>
              <dd className="max-w-[65%] break-words text-right text-foreground">
                {formatSpecValue(key, value)}
              </dd>
            </div>
          )
        )}
      </dl>
    </section>
  );
  const marketStatItems = [
    {
      label: "52-week high",
      value: marketStats?.weekHigh52 ? formatWholeDollars(marketStats.weekHigh52) : "—",
    },
    {
      label: "52-week low",
      value: marketStats?.weekLow52 ? formatWholeDollars(marketStats.weekLow52) : "—",
    },
    {
      label: "Sales tracked",
      value: marketStats ? marketStats.totalSales.toLocaleString("en-US") : "—",
    },
    {
      label: "Vs. launch price",
      value:
        vsLaunchPrice == null
          ? "—"
          : `${vsLaunchPrice >= 0 ? "+" : "−"}${Math.abs(vsLaunchPrice).toFixed(1)}%`,
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoTags metadata={seoMetadata} />
      <Navbar />

      <main className="flex-1">
        <div className="container pt-5 md:pt-6">
          <nav aria-label="Breadcrumb" className="text-[0.8rem] text-[#6E6E73] dark:text-zinc-400">
            <Link href="/" className="hover:text-foreground">
              Home
            </Link>
            <span aria-hidden="true" className="mx-2">
              ›
            </span>
            {marketTitle ? (
              <>
                <Link href={marketPath} className="hover:text-foreground">
                  {marketTitle}
                </Link>
                <span aria-hidden="true" className="mx-2">
                  ›
                </span>
              </>
            ) : null}
            <span aria-current="page" className="text-foreground">
              {product.marketModel}
            </span>
          </nav>

          <div className="mt-4 lg:hidden">{productHeader}</div>

          <div className="mt-5 grid gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-start lg:gap-14">
            {/* Image and details */}
            <div className="lg:sticky lg:top-28">
              <div className="flex aspect-[1.1] items-center justify-center overflow-hidden rounded-[1.75rem] bg-[#F5F5F7] dark:bg-white/[0.04] sm:aspect-square">
                {product.imageUrl ? (
                  <img
                    src={productImageSrc}
                    alt={product.name}
                    draggable={false}
                    className="max-h-[80%] max-w-[72%] select-none object-contain mix-blend-multiply dark:rounded-3xl dark:bg-white dark:p-6 dark:mix-blend-normal"
                    onContextMenu={preventDefault}
                    onDragStart={preventDefault}
                  />
                ) : (
                  <span className="flex h-32 w-32 items-center justify-center rounded-full bg-white text-4xl font-semibold text-[#6E6E73] dark:bg-white/10">
                    {product.brand[0]}
                  </span>
                )}
              </div>
              <div className="mt-5 hidden lg:block">{productDetailsCard}</div>
            </div>

            {/* Price, switches and marketplace */}
            <div>
              <div className="hidden lg:block">{productHeader}</div>

              <section
                aria-label="Market price"
                className="border-t border-[#E8E8ED] pt-6 dark:border-white/10 lg:mt-7"
              >
                <p className="text-[0.8rem] text-[#6E6E73] dark:text-zinc-400">
                  Market price
                  {canSwitchPrices ? (
                    <>
                      {" · "}
                      <span className="text-foreground">
                        {activeSelectionLabel || "All configurations"}
                      </span>
                    </>
                  ) : null}
                </p>
                <div className="mt-1.5 flex flex-wrap items-baseline gap-x-4 gap-y-1.5">
                  <span className="font-mono text-[2.5rem] font-medium leading-[1.05] tracking-[-0.035em] text-foreground sm:text-[3.5rem]">
                    {displayedPrice == null ? "—" : formatPrice(displayedPrice)}
                  </span>
                  {hasResolvedChange ? (
                    <span
                      className={`font-mono text-[1.05rem] font-medium ${getChangeColorClass(resolvedChangePercent)}`}
                    >
                      {isPositive ? "+" : "−"}
                      {formatPrice(Math.abs(resolvedPriceChangeAmount))} ({isPositive ? "+" : "−"}
                      {Math.abs(resolvedChangePercent).toFixed(2)}%)
                    </span>
                  ) : null}
                  {marketChangeAsOfLabel ? (
                    <span className="text-[0.8rem] text-[#6E6E73] dark:text-zinc-400">
                      as of {marketChangeAsOfLabel}
                    </span>
                  ) : null}
                </div>
                {priceNote ? (
                  <p className="mt-1.5 text-[0.8rem] text-[#6E6E73] dark:text-zinc-400">{priceNote}</p>
                ) : null}

                {canSwitchPrices ? (
                  <div className="mt-6 space-y-3.5">
                    {conditionOptions.length > 0 && (
                      <FilterButtonGroup
                        label="Condition"
                        options={conditionOptions}
                        selected={selectedCondition}
                        onSelect={setSelectedCondition}
                      />
                    )}
                    {storageOptions.length > 0 && (
                      <FilterButtonGroup
                        label="Storage"
                        options={storageOptions}
                        selected={selectedStorage}
                        onSelect={setSelectedStorage}
                      />
                    )}
                    {displaySizeOptions.length > 1 && (
                      <FilterButtonGroup
                        label="Display size"
                        options={displaySizeOptions}
                        selected={selectedDisplaySize}
                        onSelect={setSelectedDisplaySize}
                      />
                    )}
                    {carrierOptions.length > 0 && (
                      <FilterButtonGroup
                        label={product.deviceType === "tablets" ? "Connectivity" : "Carrier"}
                        options={carrierOptions}
                        selected={selectedCarrier}
                        onSelect={setSelectedCarrier}
                      />
                    )}
                  </div>
                ) : null}
              </section>

              <dl className="mt-7 grid grid-cols-2 gap-x-4 gap-y-5 border-t border-[#E8E8ED] pt-5 dark:border-white/10 sm:grid-cols-4">
                {marketStatItems.map(stat => (
                  <div key={stat.label}>
                    <dt className="text-[0.8rem] text-[#6E6E73] dark:text-zinc-400">{stat.label}</dt>
                    <dd className="mt-1.5 font-mono text-xl font-medium text-foreground">
                      {stat.value}
                    </dd>
                  </div>
                ))}
              </dl>

              <section
                aria-label="Marketplace"
                className="mt-7 rounded-[1.25rem] bg-[#F5F5F7] p-5 dark:bg-white/[0.04] sm:px-[1.375rem]"
              >
                <div className="flex flex-wrap items-center gap-x-7 gap-y-4">
                  <dl className="flex gap-7">
                    <div>
                      <dt className="text-xs text-[#6E6E73] dark:text-zinc-400">Lowest ask</dt>
                      <dd className="mt-1 font-mono text-lg font-medium text-foreground">
                        {lowestAsk ? formatPrice(lowestAsk) : "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-[#6E6E73] dark:text-zinc-400">Highest bid</dt>
                      <dd className="mt-1 font-mono text-lg font-medium text-foreground">
                        {highestBid ? formatPrice(highestBid) : "—"}
                      </dd>
                    </div>
                  </dl>
                  <p className="min-w-[11rem] flex-1 text-[0.8rem] leading-5 text-[#424245] dark:text-zinc-300">
                    {liveListingStats.total === 0
                      ? "No one is selling this model on techfare yet. List yours at the market price."
                      : `${liveListingStats.total} live listing${liveListingStats.total === 1 ? "" : "s"} on techfare.`}
                  </p>
                  <div className="flex gap-2.5">
                    {hasLowestAskAvailable ? (
                      <a
                        href="#live-listings"
                        className={`${pillClassName} border border-[#D2D2D7] bg-white text-foreground hover:border-foreground dark:border-white/15 dark:bg-transparent`}
                      >
                        Place a bid
                      </a>
                    ) : (
                      <button
                        type="button"
                        disabled
                        className={`${pillClassName} cursor-not-allowed border border-[#D2D2D7] bg-white text-[#86868B] dark:border-white/10 dark:bg-transparent dark:text-zinc-500`}
                      >
                        Place a bid
                      </button>
                    )}
                    <Link
                      href={sellThisProductHref}
                      className={`${pillClassName} bg-foreground text-background hover:opacity-90`}
                    >
                      Sell yours
                    </Link>
                  </div>
                </div>
              </section>

              <div className="mt-7 lg:hidden">{productDetailsCard}</div>
            </div>
          </div>
        </div>

        {/* Price History */}
        <section aria-labelledby="price-history" className="container pt-14 md:pt-[4.5rem]">
          <div className="rounded-3xl border border-[#E8E8ED] px-4 pb-5 pt-5 dark:border-white/10 sm:px-[1.875rem] sm:pb-6 sm:pt-[1.625rem]">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2
                  id="price-history"
                  className="text-xl font-semibold tracking-[-0.015em] text-foreground"
                >
                  Price history
                </h2>
                {priceStats ? (
                  <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[0.8rem]">
                    {[
                      ["High", formatWholeDollars(priceStats.high)],
                      ["Low", formatWholeDollars(priceStats.low)],
                      ["Average", formatWholeDollars(priceStats.avg)],
                    ].map(([label, value]) => (
                      <div key={label} className="flex gap-1.5">
                        <dt className="text-[#6E6E73] dark:text-zinc-400">{label}</dt>
                        <dd className="font-mono text-foreground">{value}</dd>
                      </div>
                    ))}
                    <div className="flex gap-1.5">
                      <dt className="text-[#6E6E73] dark:text-zinc-400">Change</dt>
                      <dd className={`font-mono ${getChangeColorClass(priceStats.change)}`}>
                        {priceStats.change >= 0 ? "+" : "−"}
                        {formatWholeDollars(Math.abs(priceStats.change))} (
                        {priceStats.change >= 0 ? "+" : "−"}
                        {Math.abs(priceStats.changePercent)}%)
                      </dd>
                    </div>
                  </dl>
                ) : null}
              </div>
              <div
                role="group"
                aria-label="Chart range"
                className="flex gap-0.5 rounded-full bg-[#F5F5F7] p-[3px] dark:bg-white/[0.06]"
              >
                {timeRanges.map(range => {
                  const isDisabled = !hasExtendedHistoryAccess && isLockedTimeRange(range);
                  const isActive = effectiveTimeRange === range;

                  return (
                    <button
                      key={range}
                      type="button"
                      disabled={isDisabled}
                      aria-pressed={isActive}
                      onClick={() => setTimeRange(range)}
                      className={`h-[1.875rem] rounded-full px-3.5 text-[0.8rem] font-medium transition-colors ${
                        isActive
                          ? "bg-white font-semibold text-foreground shadow-[0_1px_3px_rgba(17,17,19,0.12)] dark:bg-white/[0.14]"
                          : "text-[#6E6E73] hover:text-foreground dark:text-zinc-400"
                      } ${isDisabled ? "cursor-not-allowed opacity-45 hover:text-[#6E6E73]" : ""}`}
                    >
                      {range === "ALL" ? "All" : range}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-5 h-64 [touch-action:none] sm:h-72">
              {chartData.length > 0 ? (
                <Suspense fallback={<div className="h-full" />}>
                  <ProductPriceChart
                    data={chartData}
                    colors={chartColors}
                    isMobile={isMobile}
                    trendLabel={`${chartTrendDays}-day median`}
                    formatAxisDate={formatChartDateLabel}
                    formatTooltipDate={formatDate}
                    formatPrice={formatPrice}
                  />
                </Suspense>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-[#6E6E73] dark:text-zinc-400">
                  {priceHistory ? "No sales in this range yet." : "Loading chart…"}
                </div>
              )}
            </div>
            <p className="mt-3 text-xs leading-5 text-[#6E6E73] dark:text-zinc-400">
              {chartTrendDays}-day median of daily sold prices on eBay and techfare; the faint
              line is each day's average.
              {!authLoading && !hasExtendedHistoryAccess ? (
                <>
                  {" "}
                  <a href={getLoginUrl()} className="text-primary hover:underline">
                    Sign in
                  </a>{" "}
                  or{" "}
                  <a href={getSignupUrl()} className="text-primary hover:underline">
                    sign up
                  </a>{" "}
                  to see the 1Y and All ranges.
                </>
              ) : null}
            </p>
          </div>
        </section>

        {/* Recent Sales and Live Listings */}
        <section className="container grid gap-10 pt-12 md:pt-14 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:items-start">
          <div aria-labelledby="recent-sales" role="region">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <h2
                id="recent-sales"
                className="text-xl font-semibold tracking-[-0.015em] text-foreground"
              >
                Recent sales
              </h2>
              <span className="text-[0.8rem] text-[#6E6E73] dark:text-zinc-400">
                Vs. market compares each sale with the price above
              </span>
            </div>

            {!authLoading && !hasRecentSalesAccess ? (
              <div className="relative mt-3.5 overflow-hidden">
                <div aria-hidden="true" className="pointer-events-none select-none blur-[5px]">
                  {mockRecentSales.map(sale => (
                    <div
                      key={sale.id}
                      className="grid min-h-12 items-center gap-3 border-b border-[#F0F0F3] text-sm dark:border-white/[0.06]"
                      style={{ gridTemplateColumns: signedOutRecentSalesColumns }}
                    >
                      <span>
                        {new Date(sale.soldAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      <span>{formatConditionLabel(sale.condition)}</span>
                      {hasStorageDimension ? <span>—</span> : null}
                      <span className="text-right font-mono">{formatPrice(sale.price)}</span>
                      <span className="text-right font-mono">
                        {sale.diffPercent >= 0 ? "+" : "−"}
                        {Math.abs(sale.diffPercent)}%
                      </span>
                    </div>
                  ))}
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/50 px-6 text-center">
                  <p className="text-[0.95rem] font-semibold text-foreground">
                    Sign in to see recent sales
                  </p>
                  <p className="mt-1 max-w-[22rem] text-sm text-[#424245] dark:text-zinc-300">
                    Every sale with its condition, storage and price against the market.
                  </p>
                  <div className="mt-4 flex gap-2.5">
                    <a
                      href={getLoginUrl()}
                      className={`${pillClassName} border border-[#D2D2D7] bg-white text-foreground hover:border-foreground dark:border-white/15 dark:bg-transparent`}
                    >
                      Log in
                    </a>
                    <a
                      href={getSignupUrl()}
                      className={`${pillClassName} bg-foreground text-background hover:opacity-90`}
                    >
                      Sign up
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="mt-3.5 border-t border-[#E8E8ED] dark:border-white/10">
                  <div
                    className="grid h-[2.375rem] grid-cols-[4rem_minmax(0,1fr)_5.5rem_4.5rem] items-center gap-3 border-b border-[#E8E8ED] text-xs text-[#6E6E73] dark:border-white/10 dark:text-zinc-400 sm:grid-cols-[var(--sales-columns)]"
                    style={{ "--sales-columns": recentSalesColumns } as CSSProperties}
                  >
                    <span>Date</span>
                    <span>Condition</span>
                    {hasStorageDimension ? <span className="hidden sm:block">Storage</span> : null}
                    {showsSaleCarrier ? <span className="hidden sm:block">Carrier</span> : null}
                    <span className="text-right">Price</span>
                    <span className="text-right">Vs. market</span>
                  </div>
                  {visibleRecentSales.map(sale => {
                    const diffPercent =
                      recentSalesComparisonPrice != null && recentSalesComparisonPrice > 0
                        ? ((sale.price - recentSalesComparisonPrice) / recentSalesComparisonPrice) *
                          100
                        : null;
                    const carrier = getSaleCarrier(sale.size);

                    return (
                      <div
                        key={sale.id}
                        className="grid min-h-12 grid-cols-[4rem_minmax(0,1fr)_5.5rem_4.5rem] items-center gap-3 border-b border-[#F0F0F3] py-2 text-sm dark:border-white/[0.06] sm:grid-cols-[var(--sales-columns)]"
                        style={{ "--sales-columns": recentSalesColumns } as CSSProperties}
                      >
                        <span className="text-[#424245] dark:text-zinc-300">
                          {new Date(sale.soldAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                        <span className="min-w-0">
                          <span
                            className={`block truncate ${sale.condition ? "text-foreground" : "text-[#6E6E73]"}`}
                          >
                            {formatConditionLabel(sale.condition)}
                          </span>
                          <span className="block truncate text-xs text-[#6E6E73] dark:text-zinc-400 sm:hidden">
                            {[sale.storage, carrier].filter(Boolean).join(" · ")}
                          </span>
                        </span>
                        {hasStorageDimension ? (
                          <span className="hidden text-foreground sm:block">{sale.storage ?? "—"}</span>
                        ) : null}
                        {showsSaleCarrier ? (
                          <span className="hidden truncate text-foreground sm:block">
                            {carrier ?? "—"}
                          </span>
                        ) : null}
                        <span className="text-right font-mono font-medium text-foreground">
                          {formatPrice(sale.price)}
                        </span>
                        <span
                          className={`text-right font-mono text-[0.8rem] ${
                            diffPercent == null ? "text-[#86868B]" : getChangeColorClass(diffPercent)
                          }`}
                        >
                          {diffPercent == null
                            ? "—"
                            : `${diffPercent >= 0 ? "+" : "−"}${Math.abs(diffPercent).toFixed(1)}%`}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {canShowMoreRecentSales ? (
                  <button
                    type="button"
                    onClick={() => setShowAllRecentSales(value => !value)}
                    className="mt-4 h-10 rounded-full border border-[#D2D2D7] px-[1.125rem] text-sm font-medium text-foreground transition-colors hover:border-foreground dark:border-white/15"
                  >
                    {showAllRecentSales ? "Show fewer sales" : "Show more sales"}
                  </button>
                ) : null}

                {(!recentSales || recentSales.length === 0) && (
                  <p className="py-8 text-center text-sm text-[#6E6E73] dark:text-zinc-400">
                    No recent sales yet.
                  </p>
                )}
              </>
            )}
          </div>

          <div id="live-listings" aria-labelledby="live-listings-title" role="region">
            <div className="flex items-baseline justify-between gap-3">
              <h2
                id="live-listings-title"
                className="text-xl font-semibold tracking-[-0.015em] text-foreground"
              >
                Live listings
              </h2>
              {liveListings.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setIsLiveListingsOpen(true)}
                  className="text-sm text-primary hover:underline"
                >
                  View all {liveListingStats.total}
                </button>
              ) : null}
            </div>
            {liveListings.length === 0 ? (
              <div className="mt-3.5 rounded-[1.25rem] bg-[#F5F5F7] px-7 py-8 text-center dark:bg-white/[0.04]">
                <p className="text-base font-semibold text-foreground">No live listings yet</p>
                <p className="mx-auto mt-2 max-w-[19rem] text-sm leading-6 text-[#424245] dark:text-zinc-300">
                  Be the first to sell a {valueSummary.modelName} on techfare. Your listing starts
                  from today's market price.
                </p>
                <Link
                  href={sellThisProductHref}
                  className={`${pillClassName} mt-[1.125rem] bg-foreground text-background hover:opacity-90`}
                >
                  Sell yours
                </Link>
              </div>
            ) : visibleLiveListings.length === 0 ? (
              <div className="mt-3.5 rounded-[1.25rem] bg-[#F5F5F7] px-7 py-8 text-center dark:bg-white/[0.04]">
                <p className="text-sm font-medium text-foreground">
                  No live listings match those filters.
                </p>
                <button
                  type="button"
                  onClick={resetListingFilters}
                  className="mt-3 text-sm text-primary hover:underline"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="mt-3.5 space-y-3">
                {previewLiveListings.map(listing => (
                  <LiveListingCard
                    key={listing.id}
                    listing={listing}
                    product={product}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        <Sheet
          open={isLiveListingsOpen}
          onOpenChange={setIsLiveListingsOpen}
        >
          <SheetContent
            side="right"
            className="w-full gap-0 border-[#E8E8ED] dark:border-white/10 sm:max-w-xl"
          >
            <SheetHeader className="border-b border-[#E8E8ED] px-6 pb-5 pt-6 dark:border-white/10">
              <SheetTitle className="text-[1.375rem] font-semibold tracking-[-0.02em] text-foreground">
                All live listings
              </SheetTitle>
              <SheetDescription className="text-sm text-[#424245] dark:text-zinc-300">
                Every active listing for {product.name}.
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="space-y-3">
                {listingConditionOptions.length > 0 && (
                  <FilterButtonGroup
                    label="Condition"
                    options={listingConditionOptions}
                    selected={selectedListingCondition}
                    onSelect={setSelectedListingCondition}
                  />
                )}
                {listingStorageOptions.length > 0 && (
                  <FilterButtonGroup
                    label="Storage"
                    options={listingStorageOptions}
                    selected={selectedListingStorage}
                    onSelect={setSelectedListingStorage}
                  />
                )}
                {listingCarrierOptions.length > 0 && (
                  <FilterButtonGroup
                    label="Carrier"
                    options={listingCarrierOptions}
                    selected={selectedListingCarrier}
                    onSelect={setSelectedListingCarrier}
                  />
                )}
              </div>

              <div className="mt-5 flex items-baseline justify-between gap-3 border-t border-[#F0F0F3] pt-4 dark:border-white/[0.06]">
                <p className="text-sm text-[#6E6E73] dark:text-zinc-400">
                  <span className="font-mono text-foreground">{visibleLiveListings.length}</span>{" "}
                  matching listing{visibleLiveListings.length === 1 ? "" : "s"}
                </p>
                {hasActiveListingFilters && (
                  <button
                    type="button"
                    onClick={resetListingFilters}
                    className="text-sm text-primary hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>

              <div className="mt-3 space-y-3">
                {visibleLiveListings.length === 0 ? (
                  <div className="rounded-[1.25rem] bg-[#F5F5F7] px-6 py-8 text-center dark:bg-white/[0.04]">
                    <p className="text-sm font-medium text-foreground">
                      No live listings match those filters.
                    </p>
                    <p className="mt-1 text-sm text-[#424245] dark:text-zinc-300">
                      Clear the filters or check back for new listings.
                    </p>
                  </div>
                ) : (
                  visibleLiveListings.map(listing => (
                    <LiveListingCard
                      key={listing.id}
                      listing={listing}
                      product={product}
                    />
                  ))
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Questions */}
        {valueFaq.length > 0 ? (
          <section aria-labelledby="product-faq" className="container pt-16 md:pt-[5.5rem]">
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-12">
              <TwoToneHeading
                id="product-faq"
                title="Questions."
                subtitle={`About ${valueSummary.modelName} value.`}
                sizeClassName="text-[1.75rem] sm:text-[2.25rem]"
              />
              <div className="border-t border-[#E8E8ED] dark:border-white/10">
                {valueFaq.map((item, index) => (
                  <details
                    key={item.question}
                    open={index === 0}
                    className="group border-b border-[#E8E8ED] dark:border-white/10"
                  >
                    <summary className="flex min-h-[3.75rem] cursor-pointer list-none items-center justify-between gap-4 py-3.5 text-base font-medium text-foreground marker:content-none md:py-5 md:text-[1.05rem] [&::-webkit-details-marker]:hidden">
                      {item.question}
                      <span
                        aria-hidden="true"
                        className="shrink-0 text-2xl font-light leading-none text-[#6E6E73] transition-transform duration-200 group-open:rotate-45"
                      >
                        +
                      </span>
                    </summary>
                    <p className="max-w-[42rem] pb-5 text-sm leading-6 text-[#424245] dark:text-zinc-300 md:text-[0.95rem] md:leading-7">
                      {item.answer}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {/* Similar Models */}
        {relatedProducts.length > 0 ? (
          <section aria-labelledby="similar-models" className="container pb-16 pt-16 md:pb-24 md:pt-[5.5rem]">
            <h2
              id="similar-models"
              className="text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-[1.75rem]"
            >
              Compare similar models
            </h2>
            <div className="scrollbar-none -mx-4 mt-5 grid snap-x snap-mandatory auto-cols-[minmax(13rem,1fr)] grid-flow-col gap-4 overflow-x-auto px-4 pb-2 pt-1 [touch-action:pan-x] md:mx-0 md:px-0.5">
              {relatedProducts.map(relatedProduct => (
                <MarketProductCard
                  key={relatedProduct.id}
                  product={relatedProduct}
                  subtitle={getProductCardSubtitle(relatedProduct)}
                  history={relatedHistoriesById.get(relatedProduct.id)}
                  isHistoryLoading={relatedHistoriesLoading}
                  onClick={() => trackProductClick(relatedProduct.id, "product-detail-related")}
                  className="snap-start"
                />
              ))}
            </div>
          </section>
        ) : (
          <div className="pb-16 md:pb-24" />
        )}
      </main>

      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  );
}
